import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Checkbox, Divider, Grid, GridItem, HStack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import SingleOptionButton from '../../../../../components/Modal/ModalButtonGroups/SingleOptionButton';
import LoadingSpinner from '../../../../../components/Spinner';
import { getCompanyCodes, selectCompanyCodes } from '../../../../../redux/modules/company';
import {
  selectCheckboxMap,
  setSelectedCheckboxCodes
} from '../../../../../redux/modules/emails';
import { hideModal } from '../../../../../redux/modules/modal';
import colors from '../../../../../styles/theme/foundation/colors';
import { chunkArray, createMap } from '../../../../../utils/arrayUtil';

function Content({ type, onClose }) {
  const dispatch = useDispatch();
  const { companyId } = useParams();

  const companyCodes = useSelector(selectCompanyCodes);
  const checkboxCodes = useSelector(selectCheckboxMap);
  const [checkedCodes, setCheckedCodes] = useState(checkboxCodes);

  useEffect(() => {
    if (companyCodes?.length) {
      setCheckedCodes(checkboxCodes);
    } else {
      dispatch(getCompanyCodes(companyId));
    }
  }, [companyCodes?.length]); // eslint-disable-line

  if (!companyCodes?.length) {
    return (<LoadingSpinner />);
  }

  let chunkSize = companyCodes?.length / 2;
  if (chunkSize % 2 !== 0) {
    chunkSize += 1;
  }

  const chunkedCompanyCodes = chunkArray(companyCodes, chunkSize);

  const handleApply = () => {
    dispatch(setSelectedCheckboxCodes(checkedCodes));
    type === 'modal' ? dispatch(hideModal()) : onClose();
  };

  const handleCancel = () => (type === 'modal' ? dispatch(hideModal()) : onClose());

  const handleSingleCheckbox = (e, codeId) => {
    setCheckedCodes({ ...checkedCodes, [codeId]: { ...checkedCodes[codeId], checked: e.target.checked } });
  };

  const handleSelectAll = e => {
    const updatedCheckboxes = Object.values(checkedCodes).map(code => ({ ...code, checked: e.target.checked }));
    const updatedCheckboxMap = createMap(updatedCheckboxes, 'id');
    setCheckedCodes(updatedCheckboxMap);
  };

  return (
    <Box color={colors.greyScale['900']}>
      <Text
        mb={2}
        textStyle="bodyXl500"
      >
        Select Codes to Display:
      </Text>
      <Text />
      <Box>
        The codes selected below will display when using the
        <Box as="span" fontWeight="semibold">{' {{items_with_codes}} '}</Box>
        token.
      </Box>
      <Text
        textStyle="bodyLg500"
      >
        Note: If no codes are selected but the token is used in the email, the default will be to display all codes.
      </Text>

      { Object.keys(checkedCodes).length && (
        <Box py={4}>
          <Box width="50%">
            <Grid
              p={4}
              templateColumns="repeat(10, 1fr)"
            >
              <GridItem colSpan={1}>
                <Checkbox
                  isChecked={Object.values(checkedCodes).every(code => !!code.checked)}
                  mb={0}
                  pt="0.2rem"
                  onChange={e => handleSelectAll(e)}
                />
              </GridItem>
              <GridItem colSpan={9} fontWeight="semibold">
                Select All
              </GridItem>
            </Grid>
          </Box>
          <HStack
            alignItems="flex-start"
            gap={2}
          >
            {chunkedCompanyCodes.map((codeGroup, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box fontSize="sm" key={`${i}-chunk`} width="50%">
                {codeGroup.map(code => (
                  <Box key={code.id}>
                    <Grid
                      p={4}
                      templateColumns="repeat(10, 1fr)"
                    >
                      <GridItem colSpan={1}>
                        <Checkbox
                          isChecked={checkedCodes[code.id].checked}
                          pt="0.2rem"
                          onChange={e => handleSingleCheckbox(e, code.id)}
                        />
                      </GridItem>
                      <GridItem colSpan={1} fontWeight="semibold">
                        {code.letter}
                      </GridItem>
                      <GridItem colSpan={8}>
                        {code.description}
                      </GridItem>
                    </Grid>
                    <Divider />
                  </Box>
                ))}
              </Box>
            ))}
          </HStack>
        </Box>
      )}

      <Box my={4}>
        <SingleOptionButton
          dataAnalyticsId="apply-selected-email-codes"
          label="Apply"
          onClick={() => handleApply()}
        />
      </Box>
    </Box>
  );
}

Content.propTypes = {
  type: PropTypes.oneOf(['modal', 'popover']).isRequired,
  onClose: PropTypes.func
};

export default Content;
